import { useState, useEffect, useMemo } from 'react';
import "./BroadcastReport.css";
import Announcement1 from '../images/AskHRAdminPortal-Wide (3).png';


export default function BroadcastReport() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    // ── Search & Filter state ────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [filterAudience, setFilterAudience] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    useEffect(() => {
        fetchReports();
    }, []);

    async function fetchReports() {
        try {
            const response = await fetch('/api/broadcast-audit-report', {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch reports');
            const data = await response.json();
            setReports(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleDownload(reportId, subject) {
        setDownloadingId(reportId);
        try {
            const response = await fetch(`/api/broadcast-audit-report/${reportId}/download`, {
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to download');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Broadcast_Report_${subject || reportId}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            alert('Failed to download: ' + err.message);
        } finally {
            setDownloadingId(null);
        }
    }

    // ── Unique audiences for dropdown ────────────────────────
    const uniqueAudiences = useMemo(() => {
        const set = new Set(reports.map(r => r.ActualData?.audienceDetails?.name).filter(Boolean));
        return Array.from(set).sort();
    }, [reports]);

    // ── Filtered reports ─────────────────────────────────────
    const filteredReports = useMemo(() => {
        return reports.filter(report => {
            const subject     = report.ActualData?.subject || '';
            const email       = report.ActualData?.currentUser?.email || '';
            const displayName = report.ActualData?.currentUser?.displayName || '';
            const audience    = report.ActualData?.audienceDetails?.name || '';
            const dateStr     = report.ActualData?.sentDateFormatted || '';
            const reportDate  = report.Date ? new Date(report.Date.$date || report.Date) : null;

            const q = searchQuery.toLowerCase();
            const matchesSearch = !q || [subject, email, displayName, audience, dateStr]
                .some(val => val.toLowerCase().includes(q));

            const matchesAudience = !filterAudience || audience === filterAudience;

            const matchesDateFrom = !filterDateFrom || (reportDate && reportDate >= new Date(filterDateFrom));
            const matchesDateTo   = !filterDateTo   || (reportDate && reportDate <= new Date(filterDateTo + 'T23:59:59'));

            return matchesSearch && matchesAudience && matchesDateFrom && matchesDateTo;
        });
    }, [reports, searchQuery, filterAudience, filterDateFrom, filterDateTo]);

    // ── Active filter count badge ────────────────────────────
    const activeFilters = [filterAudience, filterDateFrom, filterDateTo].filter(Boolean).length;

    function clearAllFilters() {
        setSearchQuery('');
        setFilterAudience('');
        setFilterDateFrom('');
        setFilterDateTo('');
    }

    if (loading) return (
        <div className="br-wrapper">
            <div className="br-loading">
                <div className="br-spinner" />
                <span>Loading reports...</span>
            </div>
        </div>
    );

    if (error) return (
        <div className="br-wrapper">
            <div className="br-error">⚠️ {error}</div>
        </div>
    );

    return (
        <div className="br-wrapper">

            {/* ── Header ───────────────────────────────────── */}
            <div className="br-header">
                <div className="br-header-left">
                    <div className="br-badge">
                        <span className="br-badge-dot" />
                        Audit
                    </div>
                    <h1>Broadcast Audit Report</h1>
                    <p>Track all broadcast notifications sent across the platform</p>
                </div>
                {/* <div className="br-stats-row">
                    <div className="br-stat-pill">
                        <span className="br-stat-val">{reports.length}</span>
                        <span className="br-stat-lbl">Total Broadcasts</span>
                    </div>
                    <div className="br-stat-pill">
                        <span className="br-stat-val">
                            {reports.reduce((sum, r) => sum + (r.sent || 0), 0).toLocaleString()}
                        </span>
                        <span className="br-stat-lbl">Total Sent</span>
                    </div>
                    <div className="br-stat-pill">
                        <span className="br-stat-val">
                            {reports.reduce((sum, r) => sum + (r.failed || 0), 0).toLocaleString()}
                        </span>
                        <span className="br-stat-lbl">Total Failed</span>
                    </div>
                </div> */}
            </div>

            {/* ── Search & Filter Bar ──────────────────────── */}
            <div className="br-search-section">
                <div className="br-search-row">

                    {/* Search input */}
                    <div className="br-search-box">
                        <svg className="br-search-icon" viewBox="0 0 20 20" fill="none">
                            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
                            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                        <input
                            type="text"
                            className="br-search-input"
                            placeholder="Search by subject, name, email or audience..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="br-search-clear" onClick={() => setSearchQuery('')}>
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Filter toggle button */}
                    <button
                        className={`br-filter-btn ${isFilterOpen ? 'active' : ''}`}
                        onClick={() => setIsFilterOpen(p => !p)}
                    >
                        <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                            <path d="M3 5h14M6 10h8M9 15h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                        Filters
                        {activeFilters > 0 && (
                            <span className="br-filter-badge">{activeFilters}</span>
                        )}
                    </button>

                    {/* Results count */}
                    <div className="br-results-count">
                        {filteredReports.length === reports.length
                            ? `${reports.length} records`
                            : `${filteredReports.length} of ${reports.length} records`
                        }
                    </div>

                    {/* Clear all */}
                    {(searchQuery || activeFilters > 0) && (
                        <button className="br-clear-all" onClick={clearAllFilters}>
                            Clear all
                        </button>
                    )}
                </div>

                {/* Expanded filter panel */}
                {isFilterOpen && (
                    <div className="br-filter-panel">
                        <div className="br-filter-group">
                            <label className="br-filter-label">Audience</label>
                            <select
                                className="br-filter-select"
                                value={filterAudience}
                                onChange={e => setFilterAudience(e.target.value)}
                            >
                                <option value="">All</option>
                                {uniqueAudiences.map(a => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>

                        <div className="br-filter-group">
                            <label className="br-filter-label">Date From</label>
                            <input
                                type="date"
                                className="br-filter-date"
                                value={filterDateFrom}
                                onChange={e => setFilterDateFrom(e.target.value)}
                            />
                        </div>

                        <div className="br-filter-group">
                            <label className="br-filter-label">Date To</label>
                            <input
                                type="date"
                                className="br-filter-date"
                                value={filterDateTo}
                                onChange={e => setFilterDateTo(e.target.value)}
                            />
                        </div>

                        {activeFilters > 0 && (
                            <button className="br-filter-reset" onClick={() => {
                                setFilterAudience('');
                                setFilterDateFrom('');
                                setFilterDateTo('');
                            }}>
                                Reset Filters
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── Table ───────────────────────────────────── */}
            <div className="br-table-wrapper">
                {filteredReports.length === 0 ? (
                    <div className="br-empty">
                        <svg viewBox="0 0 48 48" fill="none" width="48" height="48">
                            <circle cx="24" cy="24" r="20" stroke="#334155" strokeWidth="2"/>
                            <path d="M16 24h16M24 16v16" stroke="#475569" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <p>No records match your search.</p>
                        <button className="br-clear-all" onClick={clearAllFilters}>Clear filters</button>
                    </div>
                ) : (
                    <table className="br-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Subject</th>
                                <th>Sent by</th>
                                <th>Audience</th>
                                <th>Download Report</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReports.map((report) => {
                                // const subject     = report.ActualData?.subject || '(No Subject)';
                                const subject = report.ActualData?.subject || report.ActualData?.EditorSubject || '(No Subject)';
                                const sentByEmail = report.ActualData?.currentUser?.email || 'N/A';
                                const displayName = report.ActualData?.currentUser?.displayName || sentByEmail;
                                const audience    = report.ActualData?.audienceDetails?.name;
                                const userCount   = report.ActualData?.audienceDetails?.userCount;
                                const date        = report.ActualData?.sentDateFormatted ||
                                    (report.Date ? new Date(report.Date.$date || report.Date).toLocaleString() : 'N/A');

                                return (
                                    <tr key={report._id}>
                                        <td>
                                            <div className="br-date">{date}</div>
                                        </td>
                                        <td>
                                            <div className="br-subject" title={subject}>
                                                {subject}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="br-user">
                                                <div className="br-user-avatar">
                                                    {displayName.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="br-user-name">{displayName}</div>
                                                    {/* <div className="br-user-email">{sentByEmail}</div> */}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {audience
                                                ? <span className="br-country-pill" title={userCount ? `${userCount} users` : ''}>
                                                    {audience}
                                                  </span>
                                                : <span className="br-country-pill br-country-pill--manual">
                                                    Manual
                                                  </span>
                                            }
                                        </td>
                                        <td>
                                            <button
                                                className="br-download-btn"
                                                onClick={() => handleDownload(report._id, subject)}
                                                disabled={downloadingId === report._id}
                                            >
                                                {downloadingId === report._id
                                                    ? <>⏳ Downloading...</>
                                                    : <>⬇️ Download Excel</>
                                                }
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}